import { useEffect, useState } from "react";
import { X, UserPlus } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Panel, StarDivider } from "@/components/common";
import { toast } from "sonner";

const emptyPlayer = () => ({ discord_username: "", activision_id: "", discord_id: "", kd: "" });

export function ManualRegister({ tournament, onClose, onDone }) {
  const size = tournament.max_players || 3;
  const requiresClan = tournament.type !== "INTERNAL_SEALS";
  const [teamName, setTeamName] = useState("");
  const [tag, setTag] = useState("");
  const [clans, setClans] = useState([]);
  const [clanId, setClanId] = useState("");
  const [clansLoading, setClansLoading] = useState(true);
  const [players, setPlayers] = useState(Array.from({ length: size }, emptyPlayer));
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const kdRequirement = (
    tournament.kd_requirement ||
    (tournament.require_kd ? "verified" : "none")
  );
  const requireKd = ["indicative", "verified"].includes(
    kdRequirement
  );

  useEffect(() => {
    if (!requiresClan) {
      setClansLoading(false);
      return undefined;
    }

    let active = true;

    api.get("/clans", { params: { active_only: true } })
      .then(({ data }) => {
        if (!active) return;
        setClans(data);
        if (data.length === 1) setClanId(data[0].id);
      })
      .catch((error) => {
        if (active) {
          toast.error(formatError(error.response?.data?.detail));
        }
      })
      .finally(() => {
        if (active) setClansLoading(false);
      });

    return () => {
      active = false;
    };
  }, [requiresClan]);

  const setP = (i, k, v) => setPlayers((ps) => ps.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));

  const submit = async () => {
    if (!teamName.trim()) { toast.error("Nome squadra obbligatorio"); return; }
    if (requiresClan && !clanId) { toast.error("Clan obbligatorio"); return; }
    for (const [i, p] of players.entries()) {
      if (!p.activision_id.trim()) {
        toast.error(`Activision ID obbligatorio per il giocatore ${i + 1}`); return;
      }
      if (p.discord_id.trim() && !/^\d+$/.test(p.discord_id.trim())) {
        toast.error(`Discord ID non valido per il giocatore ${i + 1}: deve essere numerico`); return;
      }
      if (requireKd && (p.kd === "" || isNaN(+p.kd))) {
        toast.error(`K/D obbligatorio (numerico) per il giocatore ${i + 1}`); return;
      }
    }
    if (!rulesAccepted) { toast.error("Devi accettare il Regolamento del torneo"); return; }
    const payload = {
      tournament_id: tournament.id, team_name: teamName.trim(), team_tag: tag.trim(),
      clan_id: requiresClan ? clanId : null,
      captain_discord_id: players[0].discord_id.trim() || null, rules_accepted: true,
      players: players.map((p) => ({
        discord_id: p.discord_id.trim() || null,
        discord_username: p.discord_username.trim() || null,
        activision_id: p.activision_id.trim(),
        ...(requireKd || p.kd !== "" ? { kd: +p.kd } : {}),
      })),
    };
    setSaving(true);
    try {
      await api.post("/teams/manual-register", payload);
      toast.success(`Squadra "${teamName}" iscritta`);
      onDone && onDone(); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const input = (val, onChange, ph, testid, type = "text") => (
    <input type={type} value={val} onChange={(e) => onChange(e.target.value)} placeholder={ph} data-testid={testid}
      className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white text-sm focus:border-[var(--gold)] focus:outline-none" />
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="manual-register-modal">
      <Panel gold className="p-6 w-full max-w-3xl my-8 boot-in">
        <div className="flex justify-between items-center mb-1">
          <h3 className="heading text-3xl gold-text">Iscrivi Squadra</h3>
          <button onClick={onClose} data-testid="manual-close" className="text-[var(--silver)] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="font-rajdhani text-xs text-[var(--silver)] uppercase tracking-widest mb-4">
          {tournament.name} · {size} giocatori{requireKd ? " · K/D richiesto" : ""}
        </p>

        <div className={`grid ${requiresClan ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3 mb-4`}>
          <div><label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Nome Squadra *</label>{input(teamName, setTeamName, "Es. Wolves", "manual-team-name")}</div>
          <div><label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Tag</label>{input(tag, setTag, "WLV", "manual-team-tag")}</div>
          {requiresClan && (
            <div>
              <label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Clan *</label>
              <select
                data-testid="manual-clan"
                value={clanId}
                onChange={(event) => setClanId(event.target.value)}
                disabled={clansLoading}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white text-sm font-rajdhani focus:border-[var(--gold)] focus:outline-none disabled:opacity-50"
              >
                <option value="">
                  {clansLoading ? "Caricamento…" : "Seleziona Clan"}
                </option>
                {clans.map((clan) => (
                  <option key={clan.id} value={clan.id}>
                    {clan.name} [{clan.tag}]
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {requiresClan && !clansLoading && clans.length === 0 && (
          <p className="font-rajdhani text-sm text-[var(--destructive)] mb-4">
            Nessun Clan attivo disponibile. Creane uno dalla pagina Clan.
          </p>
        )}

        <StarDivider label="Giocatori" className="mb-3" />
        <div className="space-y-3">
          {players.map((p, i) => (
            <div key={i} className={`border clip-beveled-sm p-3 ${i === 0 ? "border-[var(--gold)]/50" : "border-[var(--steel)]/30"}`} data-testid={`manual-player-${i}`}>
              <p className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--gold)] mb-2">
                {i === 0 ? "Caposquadra" : `Giocatore ${i + 1}`}
              </p>
              <div className={`grid gap-2 ${requireKd ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
                {input(p.discord_username, (v) => setP(i, "discord_username", v), "Nickname Discord (opz.)", `manual-name-${i}`)}
                {input(p.activision_id, (v) => setP(i, "activision_id", v), "Activision ID *", `manual-act-${i}`)}
                {input(p.discord_id, (v) => setP(i, "discord_id", v), i === 0 ? "Discord ID numerico caposquadra (opz.)" : "Discord ID numerico (opz.)", `manual-did-${i}`)}
                {requireKd && input(p.kd, (v) => setP(i, "kd", v), "K/D", `manual-kd-${i}`, "number")}
              </div>
            </div>
          ))}
        </div>

        <label className="flex items-center gap-2 mt-5 font-rajdhani text-sm text-white cursor-pointer" data-testid="manual-rules-check">
          <input type="checkbox" checked={rulesAccepted} onChange={(e) => setRulesAccepted(e.target.checked)} className="accent-[var(--gold)] w-4 h-4" />
          Accetto il <a href="/termini" target="_blank" rel="noreferrer" className="text-[var(--gold)] underline">Regolamento del torneo</a> *
        </label>

        <div className="flex gap-3 mt-5">
          <button onClick={submit} disabled={saving || !rulesAccepted || (requiresClan && !clanId)} data-testid="manual-submit" className="btn-gold clip-beveled px-6 py-2.5 flex items-center gap-2 disabled:opacity-50"><UserPlus className="w-4 h-4" /> {saving ? "Iscrizione…" : "Iscrivi Squadra"}</button>
          <button onClick={onClose} className="btn-outline-gold clip-beveled px-6 py-2.5">Annulla</button>
        </div>
      </Panel>
    </div>
  );
}
